import React, { useState, useEffect, useMemo } from 'react';
import { DecalItem } from '../../types/gallery';
import { createGridDecals, DEFAULT_CRAZINESS, isMobileViewport } from './decalEngine';
import RandomThing from './RandomThing';
import { touchDecalImage } from '../../utils/decalCache';
import './Decals.css';

export interface DecalSpawnerProps {
  /**
   * Craziness scale (0.0 - 1.0):
   * - 0.0: 0 spawns
   * - 0.2: 8 random spawns per gallery row (baseline)
   * - 1.0: 40 random spawns per gallery row (5x baseline)
   */
  craziness?: number;
  /** Active visible row count (e.g. 5 when filtered, 35 when unfiltered) */
  rowCount?: number;
  /** Total unfiltered exhibition row count (e.g. 35) */
  totalRowCount?: number;
}

export const DecalSpawner: React.FC<DecalSpawnerProps> = ({
  craziness = DEFAULT_CRAZINESS,
  rowCount: initialRowCount,
  totalRowCount
}) => {
  const [isMobile, setIsMobile] = useState<boolean>(() => isMobileViewport());

  // Generate decals ONCE on mount for the full exhibition canvas (or fixed small rowCount like ViewerPage).
  // These decals are NEVER rerandomized when switching color filter options;
  // they only randomize on initial load, on full reload, or when navigating away and returning.
  const [allDecals] = useState<DecalItem[]>(() => {
    const targetRows = (initialRowCount && initialRowCount <= 4)
      ? initialRowCount
      : Math.max(totalRowCount || 35, initialRowCount || 35, 35);
    return createGridDecals(targetRows, craziness, isMobileViewport());
  });

  // Dynamically listen for mobile resolution detection and viewport changes (resize / orientation)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleViewportChange = () => {
      const mobile = isMobileViewport();
      setIsMobile((prev) => (prev !== mobile ? mobile : prev));
    };

    window.addEventListener('resize', handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', handleViewportChange, { passive: true });

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
    };
  }, []);

  // Layout metrics (header offset and row height) based on the max full-page structure
  const [layoutMetrics, setLayoutMetrics] = useState<{ gridTop: number; rowHeight: number }>(() => {
    const vWidth = typeof window !== 'undefined' ? window.innerWidth : 1400;
    const estGridWidth = vWidth * 0.6;
    const estImageWidth = (estGridWidth * 0.94) / 3;
    const estRowHeight = Math.max(380, Math.round(estImageWidth * 1.15 + estGridWidth * 0.03));
    const estGridTop = Math.max(400, Math.round(vWidth * 0.28 + 220));
    return { gridTop: estGridTop, rowHeight: estRowHeight };
  });

  // Dynamically measure the actual real layout from DOM elements so positions align perfectly
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const measureLayout = () => {
      const container = document.querySelector('.home-page-container');
      const main = document.querySelector('.main');
      const firstRow = document.querySelector('.image-row');

      if (container && main) {
        const containerRect = container.getBoundingClientRect();
        const mainRect = main.getBoundingClientRect();
        const measuredGridTop = Math.max(0, mainRect.top - containerRect.top);

        let measuredRowHeight = layoutMetrics.rowHeight;
        if (firstRow) {
          const rowRect = firstRow.getBoundingClientRect();
          if (rowRect.height > 100) {
            measuredRowHeight = rowRect.height;
          }
        }

        setLayoutMetrics((prev) => {
          if (Math.abs(prev.gridTop - measuredGridTop) > 2 || Math.abs(prev.rowHeight - measuredRowHeight) > 2) {
            return { gridTop: measuredGridTop, rowHeight: measuredRowHeight };
          }
          return prev;
        });
      }
    };

    measureLayout();

    window.addEventListener('resize', measureLayout, { passive: true });
    window.addEventListener('schz-gallery-loaded', measureLayout);

    const t1 = setTimeout(measureLayout, 100);
    const t2 = setTimeout(measureLayout, 500);
    const t3 = setTimeout(measureLayout, 1500);

    return () => {
      window.removeEventListener('resize', measureLayout);
      window.removeEventListener('schz-gallery-loaded', measureLayout);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  // Active visible row count (e.g. 5 when filtered to red, 35 when unfiltered)
  const activeRowCount = typeof initialRowCount === 'number' && initialRowCount > 0 ? initialRowCount : 35;

  // Filter decals to show only those matching currently visible rows (band 0 = header, bands 1..activeRowCount = rows)
  // When a filter cuts off rows, decals past that row simply won't render, while remaining decals never move.
  const visibleDecals = useMemo(() => {
    if (activeRowCount >= 34) {
      return allDecals;
    }
    return allDecals.filter((decal) => (decal.bandIndex ?? 0) <= activeRowCount);
  }, [allDecals, activeRowCount]);

  // Touch and renew rolling 180-day cache for all spawned decals on every visit
  useEffect(() => {
    if (!allDecals || allDecals.length === 0) return;
    const uniqueSrcs = Array.from(new Set(allDecals.map((d) => d.src).filter(Boolean)));
    uniqueSrcs.forEach((src) => touchDecalImage(src));
  }, [allDecals]);

  // Compute immutable global Y position in pixels from top of page based on the full layout structure
  const getDecalTopPx = (decal: DecalItem): number => {
    const b = decal.bandIndex ?? 0;
    const offset = typeof decal.bandOffsetPct === 'number' ? decal.bandOffsetPct : 0.5;

    if (b === 0) {
      // Header area (Band 0): Positioned within the header space
      return Math.round(offset * layoutMetrics.gridTop);
    }

    // Gallery Artwork Rows (Bands 1..35): Anchored permanently to Row (b - 1)
    return Math.round(layoutMetrics.gridTop + ((b - 1) + offset) * layoutMetrics.rowHeight);
  };

  // On mobile view, do not render decals so only the main grid and background render, keeping the grid centered
  if (isMobile) {
    return null;
  }

  return (
    <div
      className="decal-container"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'visible'
      }}
    >
      {visibleDecals.map((decal) => (
        <RandomThing
          key={decal.id}
          type={decal.category}
          imageSrc={decal.src}
          maxRotate={decal.maxRotate}
          x={decal.x}
          rotation={decal.rotation}
          scale={decal.scale}
          opacity={decal.opacity}
          style={{
            top: `${getDecalTopPx(decal)}px`
          }}
        />
      ))}
    </div>
  );
};

export default DecalSpawner;
